import { Columns3, GripVertical, Lock } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '../lib/cn';

/** One column as the chooser lists it. */
export interface ColumnChooserColumn {
  id: string;
  label: string;
  visible: boolean;
  /** Always shown and always first — no tick box, no dragging. */
  locked: boolean;
  /** Added since this layout was saved — tagged so it gets noticed. */
  isNew: boolean;
  /** Pixels when sized by hand; otherwise it fits its content. */
  width?: number;
}

export interface ColumnChooserProps {
  /** Every column in display order, visible or not. */
  columns: ColumnChooserColumn[];
  /** The whole list after a tick or a move — applied at once by the caller. */
  onChange: (columns: { id: string; visible: boolean }[]) => void;
  /** The user has their own layout, so "Reset" has something to undo. */
  hasOwnLayout: boolean;
  onResetMine: () => void;
  /** The organisation has a default of its own (otherwise the screen's). */
  hasOrganisationLayout: boolean;
  /** Shows the organisation-default actions. */
  canEditOrganisationDefault: boolean;
  onSaveOrganisationDefault: () => void;
  onResetOrganisationDefault: () => void;
  /** Let every column fit its content again — shown when any has been sized. */
  onResetWidths?: () => void;
  busy?: boolean;
  error?: string | null;
  className?: string;
}

/**
 * The "Columns" button on a grid's toolbar: tick a column to show it, drag
 * (or Alt + ↑/↓) to reorder. Changes apply straight away; saving is the
 * caller's job (`useGridLayout` saves the user's layout in the background).
 * Org admins also get "Save as organisation default" — everyone's starting
 * point, which each user can still adjust and reset back to.
 */
export function ColumnChooser({
  columns,
  onChange,
  hasOwnLayout,
  onResetMine,
  hasOrganisationLayout,
  canEditOrganisationDefault,
  onSaveOrganisationDefault,
  onResetOrganisationDefault,
  onResetWidths,
  busy = false,
  error,
  className,
}: ColumnChooserProps) {
  const [open, setOpen] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const plain = columns.map((c) => ({ id: c.id, visible: c.visible }));
  const visibleCount = columns.filter((c) => c.visible).length;
  const newCount = columns.filter((c) => c.isNew && !c.locked).length;

  const toggle = (id: string) =>
    onChange(plain.map((c) => (c.id === id ? { ...c, visible: !c.visible } : c)));

  /** Move `id` to where `targetId` is. Locked columns stay put at the front. */
  const move = (id: string, targetId: string) => {
    if (id === targetId) return;
    const from = plain.findIndex((c) => c.id === id);
    const to = plain.findIndex((c) => c.id === targetId);
    if (from < 0 || to < 0 || columns[to]!.locked) return;
    const next = [...plain];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item!);
    onChange(next);
  };

  const moveBy = (id: string, delta: -1 | 1) => {
    const at = columns.findIndex((c) => c.id === id);
    const target = columns[at + delta];
    if (target && !target.locked) move(id, target.id);
  };

  return (
    <div ref={boxRef} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="dialog"
        title="Choose and order the columns"
        className="flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-slate-600 hover:bg-slate-100"
      >
        <Columns3 className="h-3.5 w-3.5" aria-hidden="true" />
        Columns
        {newCount > 0 && (
          <span className="rounded-sm bg-blue-100 px-1 text-[9px] font-semibold uppercase text-blue-700">
            New
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Columns"
          className="absolute right-0 top-full z-40 mt-1 w-64 rounded-md border border-slate-200 bg-white py-2 text-xs shadow-lg"
        >
          <p className="px-3 pb-1.5 text-[10px] text-slate-400">
            Tick to show · drag to reorder (or Alt + ↑ / ↓)
          </p>
          <ul className="max-h-80 overflow-auto">
            {columns.map((c) => {
              const lastVisible = c.visible && visibleCount <= 1;
              return (
                <li
                  key={c.id}
                  draggable={!c.locked}
                  onDragStart={(e) => {
                    setDragId(c.id);
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  onDragOver={(e) => {
                    if (!dragId || c.locked) return;
                    e.preventDefault();
                    setOverId(c.id);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragId) move(dragId, c.id);
                    setDragId(null);
                    setOverId(null);
                  }}
                  onDragEnd={() => {
                    setDragId(null);
                    setOverId(null);
                  }}
                  onKeyDown={(e) => {
                    if (!e.altKey || c.locked) return;
                    if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      moveBy(c.id, -1);
                    } else if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      moveBy(c.id, 1);
                    }
                  }}
                  className={cn(
                    'flex items-center gap-1.5 px-2 py-1',
                    !c.locked && 'cursor-grab hover:bg-slate-50',
                    dragId === c.id && 'opacity-40',
                    overId === c.id && dragId !== c.id && 'border-t-2 border-blue-400',
                  )}
                >
                  {c.locked ? (
                    <Lock className="h-3 w-3 shrink-0 text-slate-300" aria-hidden="true" />
                  ) : (
                    <GripVertical className="h-3 w-3 shrink-0 text-slate-300" aria-hidden="true" />
                  )}
                  <label
                    className={cn(
                      'flex min-w-0 flex-1 items-center gap-2',
                      c.locked ? 'text-slate-400' : 'cursor-pointer text-slate-700',
                    )}
                    title={
                      c.locked
                        ? 'Always shown'
                        : lastVisible
                          ? 'At least one column stays shown'
                          : undefined
                    }
                  >
                    <input
                      type="checkbox"
                      checked={c.visible}
                      disabled={c.locked || lastVisible}
                      onChange={() => toggle(c.id)}
                      className="h-3.5 w-3.5 accent-slate-700"
                    />
                    <span className="truncate">{c.label}</span>
                    {c.isNew && !c.locked && (
                      <span className="ml-auto rounded-sm bg-blue-100 px-1 text-[9px] font-semibold uppercase text-blue-700">
                        New
                      </span>
                    )}
                  </label>
                </li>
              );
            })}
          </ul>

          <div className="mt-1.5 space-y-1 border-t border-slate-100 px-3 pt-2">
            <button
              type="button"
              disabled={!hasOwnLayout || busy}
              onClick={onResetMine}
              className="block text-[11px] text-slate-500 underline-offset-2 hover:text-slate-800 hover:underline disabled:cursor-default disabled:text-slate-300 disabled:no-underline"
            >
              {hasOrganisationLayout ? 'Reset to organisation default' : 'Reset to default'}
            </button>
            {onResetWidths && columns.some((c) => c.width) && (
              <button
                type="button"
                disabled={busy}
                onClick={onResetWidths}
                title="Every column fits its content again"
                className="block text-[11px] text-slate-500 underline-offset-2 hover:text-slate-800 hover:underline disabled:text-slate-300"
              >
                Reset column widths
              </button>
            )}
            {canEditOrganisationDefault && (
              <>
                <button
                  type="button"
                  disabled={busy}
                  onClick={onSaveOrganisationDefault}
                  title="Everyone starts from these columns; each user can still change their own"
                  className="block text-[11px] font-medium text-purple-600 underline-offset-2 hover:underline disabled:text-slate-300"
                >
                  Save as organisation default
                </button>
                {hasOrganisationLayout && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={onResetOrganisationDefault}
                    title="Everyone without their own layout goes back to the standard columns"
                    className="block text-[11px] text-slate-500 underline-offset-2 hover:text-slate-800 hover:underline disabled:text-slate-300"
                  >
                    Clear organisation default
                  </button>
                )}
              </>
            )}
            {error && <p className="text-[11px] text-rose-600">{error}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
